import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * devops module ServiceStores.
 *
 * Declares the Feathers services the DevOps ViewStores consume (migrated off the
 * removed `createRestApi`). ViewStores read `rootStore.<name>Store` and call
 * `.find/.get/.create/.patch/.remove` instead of the raw REST client.
 *
 * Two flavours here:
 *   - `devops-connections` is a real CRUD store: its `entities` cache IS the
 *     connection list (DevOpsConnectionsStore exposes it as a computed getter).
 *   - the rest are live read-only Azure proxies / derived endpoints
 *     (`devops-projects`, `devops-repos`, `devops-pipelines`, `devops-releases`,
 *     `devops-work-items`) or scoped writes (`devops-subscriptions`). Their rows
 *     are connection/project-scoped and re-queried on every selection change, so
 *     the boards read the `find()` RESPONSE rows (normalised/scoped projections),
 *     NOT the cross-query entity cache. `idField` is the natural Azure row id;
 *     `enableStorage: false` keeps these volatile proxies out of local storage.
 */
export const devopsStores: ServiceStoreConfig[] = [
  // Real CRUD list — the entity cache backs DevOpsConnectionsStore.connections.
  { name: "devops-connections", options: { idField: "id", enableStorage: false } },
  // Client↔Azure entity links (project/repo/pipeline) backing the Client 360
  // "DevOps" tab. Previously parked in project-management.stores.ts; it is a
  // DevOps service and outlived the project-management module.
  { name: "devops-links", options: { idField: "id", enableStorage: false } },
  // Live read-only Azure DevOps proxies (scoped by connectionId/projectId).
  // enableOffline OFF is REQUIRED: their find() throws BadRequest without a
  // connectionId, so the offline wrapper's blanket sync-on-reconnect (a bare
  // find()) can never succeed — it just logged "connectionId is required" and
  // dropped the store into offline fallback on every socket connect.
  { name: "devops-projects", options: { idField: "id", enableStorage: false, enableOffline: false } },
  // The client×project rollup the DevOps landing page and Project 360 read —
  // find-only, ONE response per call (local data, optional cached live project
  // list). Read from the find() RESPONSE like the proxies; the cache is not
  // entity-keyed in any useful way. enableOffline OFF for the same reason as
  // the proxies: a bare find() on reconnect is a full recompute nobody asked for.
  { name: "devops-overview", options: { idField: "generatedAt", enableStorage: false, enableOffline: false } },
  // The all-projects build board (Pipelines page) — find-only, ONE composite
  // response per call, read from the RESPONSE like devops-overview.
  { name: "devops-build-board", options: { idField: "generatedAt", enableStorage: false, enableOffline: false } },
  { name: "devops-repos", options: { idField: "id", enableStorage: false, enableOffline: false } },
  { name: "devops-pipelines", options: { idField: "id", enableStorage: false, enableOffline: false } },
  { name: "devops-releases", options: { idField: "id", enableStorage: false, enableOffline: false } },
  { name: "devops-work-items", options: { idField: "id", enableStorage: false, enableOffline: false } },
  // Service-hook subscriptions — scoped CRUD (find/create/remove by connection).
  { name: "devops-subscriptions", options: { idField: "id", enableStorage: false } },
];
